// hooks/chat/useChatSubmit.jsx
import { useRef } from 'react'

import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'

import { useChatStreaming } from '@/hooks/chat/useChatStreaming'
import { useApi } from '@/hooks/useApi'
import { useAllConversationsStore } from '@/store/useAllConversationsStore'
import { useConversationManager } from '@/store/useConversationManager'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'

/**
 * Handles the submission of user messages to the chat.
 *
 * @returns {Object}  submitUserMessage  function to submit a user message
 */
export function useChatSubmit() {
  const api = useApi()
  const navigate = useNavigate()
  const conversationId = useCurrentConversationStore(s => s.id)

  const addUserMessage = useCurrentConversationStore(s => s.addUserMessage)
  const setToastMessage = useCurrentConversationStore(s => s.setToastMessage)
  const { createConversationWithMessage } = useConversationManager()
  const loadConversations = useAllConversationsStore(s => s.loadConversations)
  const setCurrentId = useCurrentConversationStore(s => s.setCurrentId)
  const setMessages = useCurrentConversationStore(s => s.setMessages)
  const setLoading = useCurrentConversationStore(s => s.setLoading)
  const isLoading = useCurrentConversationStore(s => s.loading)

  const cleanupRef = useRef(() => {})

  const { streamAgentMessage } = useChatStreaming()

  async function submitUserMessage(messagePayload) {
    const payload =
      typeof messagePayload === 'string'
        ? { content: messagePayload }
        : messagePayload || {}

    const { content, file_pathnames = [], webSearchEnabled = false } = payload

    const trimmedContent = content?.trim()
    if (!trimmedContent || isLoading) return
    // abort any in-flight stream
    cleanupRef.current?.()

    const agentOptions = {
      web_search_enabled: webSearchEnabled ? 'true' : 'false',
    }

    if (file_pathnames.length > 0) {
      agentOptions.uploaded_file_info = JSON.stringify(file_pathnames)
    }

    const streamOptions = { query: agentOptions }

    if (!conversationId) {
      // create new convo + first message
      setLoading(true)
      const currentSpan = Sentry.startInactiveSpan({
        name: 'createConversationWithMessage',
      })
      currentSpan?.addEvent('createConversationWithMessage', {
        trimmedContent,
        conversation_id: conversationId,
      })
      const result = await createConversationWithMessage({
        title: 'Untitled Conversation',
        message: {
          role: 'user',
          content: trimmedContent,
        },
      })
      if (result) {
        const { id } = result.conversation

        setCurrentId(id)
        setMessages([
          {
            role: 'user',
            content: trimmedContent,
          },
        ])

        navigate(`/chat/${result.conversation.id}`)
        cleanupRef.current = await streamAgentMessage(
          result.message.chat_stream_url,
          streamOptions,
        )
      }
      setLoading(false)
      return
    }

    // post into existing convo
    setLoading(true)

    const res = await api.postMessage({
      conversation_id: conversationId,
      role: 'user',
      content: trimmedContent,
    })
    if (res.ok && res.body) {
      const { chat_stream_url, ...message } = res.body
      const currentSpan = Sentry.startInactiveSpan({
        name: 'streamAgentMessage',
      })
      addUserMessage(message)
      currentSpan.addEvent('streamAgentMessage', {
        chat_stream_url,
        streamOptions,
      })
      cleanupRef.current = await streamAgentMessage(
        chat_stream_url,
        streamOptions,
      )
      loadConversations(api)
    } else {
      const currentSpan = Sentry.startInactiveSpan({
        name: 'streamAgentMessage',
      })
      setToastMessage({
        message: res.body?.message ?? 'Failed to send message',
        type: 'error',
        variant: 'destructive',
      })
      currentSpan.addEvent('error', {
        message: res.body?.message ?? 'Failed to send message',
      })
      currentSpan.end()
    }
    setLoading(false)
  }

  return { submitUserMessage }
}
